import * as XLSX from 'xlsx';

import type { ProcessoSection } from '../pages/hooks/useProcessosSections.ts';
import {
  celulaExcelComoTexto,
  type LinhaPreviewImportNumeroProcesso,
} from './numero-processo-excel-texto.utils.ts';

export {
  celulaExcelComoTexto,
  montarItensNumeroProcessoImportados,
  type LinhaPreviewImportNumeroProcesso,
} from './numero-processo-excel-texto.utils.ts';

/** Alvos no formulário que aceitam importação de uma única coluna de número de processo (texto). */
export type ExcelImportNumeroAlvo =
  | { tipo: 'processos'; section: ProcessoSection }
  | { tipo: 'honorarios-sentenca' }
  | { tipo: 'honorarios-cumprimento' }
  | { tipo: 'oficios-requisitorios' }
  | { tipo: 'oficios-demais' };

function normalizarCabecalho(c: string): string {
  return c
    .replace(/\u00A0/g, ' ')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

function encontrarChaveNumeroProcesso(primeiraLinha: Record<string, unknown>): string | null {
  const keysOrig = Object.keys(primeiraLinha);
  if (keysOrig.length === 0) return null;

  if (keysOrig.length === 1) {
    return keysOrig[0];
  }

  type Scored = { orig: string; score: number };
  const scored: Scored[] = keysOrig.map((orig) => {
    const norm = normalizarCabecalho(orig);
    let score = 0;
    if (norm.includes('numero') || norm.includes('numer')) score += 2;
    if (norm.includes('processo')) score += 2;
    if (norm.includes('nr') && norm.includes('proc')) score += 2;
    if (norm.includes('nº') || norm.includes('n.')) score += 1;
    return { orig, score };
  });

  scored.sort((a, b) => b.score - a.score);
  if (scored[0].score >= 2) {
    return scored[0].orig;
  }

  const fallback = keysOrig.find((k) => {
    const n = normalizarCabecalho(k);
    return n.includes('numero') || n.includes('numer');
  });
  return fallback ?? null;
}

/**
 * Lê a primeira aba com uma coluna de cabeçalho identificável como número do processo.
 * Valores são tratados como texto. Linhas vazias são ignoradas.
 * Números repetidos (na planilha ou já na lista) são permitidos.
 */
export function parseNumeroProcessoExcel(
  arrayBuffer: ArrayBuffer,
): { linhas: LinhaPreviewImportNumeroProcesso[]; erroCabeçalho: string | null } {
  const wb = XLSX.read(arrayBuffer, { type: 'array' });
  const sheetName = wb.SheetNames[0];
  const sheet = wb.Sheets[sheetName];
  const rows = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, { defval: '', raw: false });

  if (rows.length === 0) {
    return { linhas: [], erroCabeçalho: 'A planilha está vazia.' };
  }

  const chave = encontrarChaveNumeroProcesso(rows[0]);
  if (!chave) {
    return {
      linhas: [],
      erroCabeçalho:
        'Não foi possível identificar a coluna do número do processo. Use um cabeçalho como "Número do processo" na primeira linha.',
    };
  }

  const linhas: LinhaPreviewImportNumeroProcesso[] = [];

  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    const numeroProcesso = celulaExcelComoTexto(row[chave]);
    if (!numeroProcesso) {
      continue;
    }

    linhas.push({
      indicePlanilha: i + 2,
      numeroProcesso,
      valido: true,
      erros: [],
    });
  }

  return { linhas, erroCabeçalho: null };
}
